import { ViewportScroller } from '@angular/common';
import { NgModule } from '@angular/core';
import {
  Event,
  NavigationEnd,
  NavigationSkipped,
  Router,
  RouterModule,
  Scroll,
} from '@angular/router';
import { LoaderOverlayModule } from '@epicuro-next/ataraxia/loader-overlay';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { FooterModule } from '@epicuro-next/content';
import { ActiveAccountGuard } from '@epicuro-next/features/accounts-selector';
import { appRoutingReady } from '@epicuro-next/navigation/actions';
import {
  navigationRoutesFeatureKey,
  reducer,
} from '@epicuro-next/navigation/reducer';
import { AuthGuard } from '@epicuro-next/platform/authentication';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import { UserTermsGuard } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Store, StoreModule } from '@ngrx/store';
import { filter, map, pairwise, startWith } from 'rxjs/operators';

import { allOtherRoutes, privateRoutes, routes } from './models/routes';
import { NavigationRoutesService } from './services';
import { UrlResolverGuard } from './services/url-resolver.guard';

const SCROLL_TO_TOP_EXCEPTIONS = ['overlay'];

@NgModule({
  providers: [NavigationRoutesService, UrlResolverGuard],
  imports: [
    FooterModule,
    LoaderOverlayModule,
    StoreModule.forFeature(navigationRoutesFeatureKey, reducer),
    RouterModule.forRoot(
      [
        {
          path: '',
          canLoad: [AuthGuard],
          canActivate: [ActiveAccountGuard, UrlResolverGuard, UserTermsGuard],
          children: [...privateRoutes],
        },
        ...routes,
        ...allOtherRoutes,
      ],
      {
        initialNavigation: 'enabledBlocking',
        enableTracing: false,
        paramsInheritanceStrategy: 'always',
        scrollPositionRestoration: 'disabled',
      },
    ),
  ],
  exports: [RouterModule],
})
export class EpicuroRoutesModule {
  constructor(
    router: Router,
    viewportScroller: ViewportScroller,
    navigationRoutesService: NavigationRoutesService,
    environmentService: EnvironmentService,
    store: Store,
  ) {
    environmentService.environmentSettings
      .pipe(
        filter(isNonNullish),
        map(({ multiSiteSupport }) => multiSiteSupport),
      )
      .subscribe((multiSiteSupport) => {
        if (multiSiteSupport === 'true') {
          navigationRoutesService.resetToSupportMultiSites();
        }
        store.dispatch(appRoutingReady());
      });

    router.events
      .pipe(
        filter((e: Event): e is Scroll => e instanceof Scroll),
        startWith(undefined),
        pairwise(),
      )
      .subscribe(([prevEvent, currentEnvent]) => {
        if (
          SCROLL_TO_TOP_EXCEPTIONS.some(
            (excludeScroll) =>
              (currentEnvent?.routerEvent instanceof NavigationEnd &&
                currentEnvent?.routerEvent.urlAfterRedirects.includes(
                  excludeScroll,
                )) ||
              (prevEvent?.routerEvent instanceof NavigationEnd &&
                prevEvent?.routerEvent.urlAfterRedirects.includes(
                  excludeScroll,
                )),
          )
        ) {
          return;
        }

        if (currentEnvent?.routerEvent instanceof NavigationSkipped) {
          return;
        }

        if (currentEnvent?.position) {
          // backward navigation
          viewportScroller.scrollToPosition(currentEnvent.position);
        } else if (currentEnvent?.anchor) {
          // anchor navigation
          viewportScroller.scrollToAnchor(currentEnvent.anchor);
        } else {
          // forward navigation
          viewportScroller.scrollToPosition([0, 0]);
          document.getElementsByTagName('epc-root')[0].scrollTo(0, 0);
        }
      });
  }
}
